import type { DjiStatus, InventoryDrone } from '@/lib/api/types';

/** Row state — maps onto DronesPanel's DOT_CLASS keys (outdated = amber). */
export type DjiLight = 'online' | 'outdated' | 'offline' | 'none';

// Matches the BE telemetry light (JOB-NID-DD 1.0.18): live ≤10 s, stale ≤30 s.
const TELEMETRY_LIVE_MS = 10_000;
const TELEMETRY_STALE_MS = 30_000;

export function linkLabel(s: DjiStatus): string {
  return s.mechanism === 'fh2'
    ? 'FlightHub2'
    : s.mechanism === 'dji_cloud_api'
      ? 'DJI Cloud API'
      : 'DJI Link';
}

export function linkLight(s: DjiStatus): { state: DjiLight; text: string } {
  if (s.link_state === 'alive') return { state: 'online', text: 'Alive' };
  if (s.link_state === 'down') return { state: 'offline', text: 'Down' };
  return { state: 'none', text: '—' };
}

export function telemetryLight(
  s: DjiStatus,
  now: number = Date.now(),
): { state: DjiLight; text: string } {
  // Switched off on purpose: say so, instead of "Offline" which reads as a fault.
  if (s.telemetry_reason === 'networkid_streaming_disabled')
    return { state: 'offline', text: 'Disabled' };
  if (!s.telemetry_last_at) return { state: 'offline', text: 'Offline' };
  const age = now - new Date(s.telemetry_last_at).getTime();
  if (age <= TELEMETRY_LIVE_MS) return { state: 'online', text: 'Live' };
  if (age <= TELEMETRY_STALE_MS) return { state: 'outdated', text: 'Stale' };
  return { state: 'offline', text: 'Offline' };
}

/** `aircraft_state` is a free string on the wire; only the values we can name
 *  are shown, anything else (incl. 'unknown') is hidden. */
export function dockAircraftState(raw: string): string | null {
  switch (raw) {
    case 'in_dock':
      return 'In dock';
    case 'not_in_dock':
      return 'Not in dock';
    case 'flying':
      return 'Flying';
    default:
      return null;
  }
}

export function dockRow(s: DjiStatus): { state: DjiLight; text: string; sub: string | null } {
  const sub = dockAircraftState(s.dock.aircraft_state);
  if (!s.dock.name) return { state: 'none', text: '—', sub };
  return {
    state: s.dock.online ? 'online' : 'offline',
    text: `${s.dock.name} · ${s.dock.online ? 'Online' : 'Offline'}`,
    sub,
  };
}

/** DJI aircraft "online" = it is actually sending telemetry (live or stale).
 *  A live FlightHub2 link alone only says FH2 is reachable, not the aircraft. */
export function djiOnline(d: InventoryDrone, now: number = Date.now()): boolean {
  return (
    d.dji_status != null &&
    d.status !== 'Decommissioned' &&
    telemetryLight(d.dji_status, now).state !== 'offline'
  );
}
